import {
  LoadingMessage,
  LoadingMessageConfig,
  Subject
} from '@app/types';
import { createUuid } from '@app/utilities';

/** Service to keep track of whether the loading modal should be displayed. */
export class LoadingService {
  readonly isLoadingChange = new Subject<boolean>();
  readonly messageChange = new Subject<LoadingMessageConfig | null>();
  private _loadingCount = 0;
  private _messages: { id: string; message: LoadingMessageConfig; }[] = [];

  /** Whether or not the loading modal should be displayed. */
  get isLoading(): boolean {
    return (
      this.loadingCount > 0
      || this.message != null
    );
  }

  /**
   * Current count of things that are loading.
   *
   * @note
   * You should always add/subtract instead of directly setting this because
   * other components could be loading things as well.
   */
  get loadingCount(): number {
    return this._loadingCount;
  }
  set loadingCount(loadingCount: number) {
    loadingCount = (loadingCount < 0) ? 0 : loadingCount;
    if (this._loadingCount === loadingCount) {
      return;
    }

    const oldIsLoading = this.isLoading;
    this._loadingCount = loadingCount;
    if (oldIsLoading !== this.isLoading) {
      this.isLoadingChange.next(this.isLoading);
    }
  }

  /** Message to display in the loading modal. */
  get message(): LoadingMessageConfig | null {
    return (!this._messages.length) ? null : (
      this._messages[0].message
    );
  }

  /** Add a loading message to the loading modal. */
  addMessage(
    message: LoadingMessage | LoadingMessageConfig,
    id: string = createUuid()
  ): string {
    const messageConfig = (typeof message === 'object' && 'message' in message) ? (
      message
    ) : { message };
    if (this._messages.some((m) => m.id === id)) {
      throw new Error(`Loading message ID already in use. ${window.l.notification.ask_for_help}`);
    }

    this._setMessages([
      { id, message: messageConfig },
      ...this._messages
    ]);

    return id;
  }

  /** Remove a loading message from the modal. Ignores duplicate calls for the same id. */
  removeMessage(id: string): void {
    const messages = this._messages.filter((m) => m.id !== id);
    if (messages.length < this._messages.length) {
      this._setMessages(messages);
    }
  }

  /** Show loading message while inner callback is running. */
  useLoading<T>(
    callback: () => T,
    message?: LoadingMessage | LoadingMessageConfig
  ): T {
    ++this.loadingCount;
    let messageId: null | string = null;
    if (message) {
      messageId = this.addMessage(message);
    }
    const stopLoading = () => {
      --this.loadingCount;
      if (messageId) {
        this.removeMessage(messageId);
      }
    };
    let error: unknown = null;
    let value: T | null = null;
    try {
      value = callback();
    } catch (caughtError) {
      error = caughtError;
    }

    if (value && value instanceof Promise) {
      // We can not use async/await without messing up the synchronous reponse.
      return value.finally(() => {
        stopLoading();
      }) as T;
    }

    stopLoading();
    if (error !== null) {
      throw error;
    }

    return value as T;
  }

  /** Wrap a callback in a lambda that will display the loading message when it is called. */
  wrapWithLoading<TArgs extends any[], TReturn>(
    callback: (...args: TArgs) => TReturn,
    message?: LoadingMessage | LoadingMessageConfig
  ): (...args: TArgs) => TReturn {
    return (...args) => {
      return this.useLoading(
        () => {
          return callback(...args);
        },
        message
      );
    };
  }

  /** Update messages and push a @see messageChange event if needed. */
  private _setMessages(
    messages: {
      id: string;
      message: LoadingMessageConfig;
    }[]
  ): void {
    const oldIsLoading = this.isLoading;
    const oldMessage = this.message;
    this._messages = messages;
    if (oldMessage !== this.message) {
      this.messageChange.next(this.message);
    }
    if (oldIsLoading !== this.isLoading) {
      this.isLoadingChange.next(this.isLoading);
    }
  }
}
